import { ColumnsType } from 'antd/es/table';
import SectionDesc from '../../../components/atoms/SectionDesc';
import CustomTable from '../../../components/atoms/CustomTable';
import { useTranslation } from 'react-i18next';
import { PromotionRequest } from '../../../types/promotions';

interface RequestsSectionProps {
  data: PromotionRequest[];
  isLoading?: boolean;
  currentPage: number;
  totalElements: number;
  pageSize?: number;
  onPageChange: (page: number, pageSize?: number) => void;
  onRowClick: (record: PromotionRequest) => void;
}

export default function RequestsSection({
  data,
  isLoading,
  currentPage,
  totalElements,
  pageSize = 10,
  onPageChange,
  onRowClick,
}: RequestsSectionProps) {
  const { t } = useTranslation();

  const columns: ColumnsType<PromotionRequest> = [
    {
      title: t('home.promo-seller'),
      dataIndex: 'seller',
      key: 'seller',
      render: (_val, record) => {
        const sellerName =
          record.sellerName ||
          (typeof record.seller === 'object' && record.seller !== null
            ? record.seller?.companyName ||
              `${record.seller?.firstName || ''} ${record.seller?.lastName || ''}`.trim()
            : record.seller) ||
          '-';
        return <span className="text-gray-900">{sellerName}</span>;
      },
    },
    {
      title: t('home.promo-product'),
      dataIndex: 'product',
      key: 'product',
      render: (_val, record) => {
        const productName =
          (typeof record.product === 'object'
            ? record.product?.name
            : record.product) ||
          record.productName ||
          '-';
        return <span>{productName}</span>;
      },
    },
    {
      title: t('home.promo-type'),
      dataIndex: 'type',
      key: 'type',
      render: (_val, record) => {
        const typeLabels: Record<string, string> = {
          TOP_PRODUCTS: t('home.top-products', 'Top products'),
          HERO_BANNER: t('home.hero-banners', 'Hero banners'),
          FLASH_DEALS: t('home.flash-deals', 'Flash deals'),
        };
        const rawType = String(
          record.promotionTypeCode || record.promotionType || record.type || ''
        ).toUpperCase();
        const type =
          typeLabels[rawType] ||
          record.promotionType ||
          record.typePlacement ||
          rawType ||
          '-';
        return <span>{type}</span>;
      },
    },
    {
      title: t('home.promo-placement'),
      dataIndex: 'placement',
      key: 'placement',
      render: (_val, record) => {
        const scopeLabels: Record<string, string> = {
          MAIN_MARKETPLACE: t('home.main-marketplace', 'Main Marketplace'),
          CATEGORY_MARKETPLACE: t(
            'home.category-marketplace',
            'Category Marketplace'
          ),
        };
        const rawScope = String(
          record.marketplaceScopeCode || record.placement || ''
        ).toUpperCase();
        const placement =
          scopeLabels[rawScope] ||
          record.placementName ||
          record.placement ||
          '-';
        return <span>{placement}</span>;
      },
    },
    {
      title: t('home.promo-duration'),
      dataIndex: 'duration',
      key: 'duration',
      render: (_val, record) => {
        if (record.duration !== undefined && record.duration !== null) {
          const isHour = record.durationUnitCode?.toUpperCase() === 'HOUR';
          const unit = isHour
            ? t('home.duration-unit-hours', 'hour')
            : t('home.duration-unit-days', 'days');
          return `${record.duration} ${unit}`;
        }
        return '-';
      },
    },
    {
      title: t('home.promo-price'),
      dataIndex: 'price',
      key: 'price',
      render: (_val, record) => {
        const val = record.price ?? record.totalAmount ?? record.amount;
        if (val !== undefined && val !== null) {
          return `${val} ${t('SAR')}`;
        }
        return '-';
      },
    },
    {
      title: t('home.status'),
      dataIndex: 'status',
      key: 'status',
      render: (_val, record) => {
        const rawStatus = String(
          record.statusCode || record.status || ''
        ).toUpperCase();
        const statusMap: Record<string, { label: string; color: string }> = {
          APPROVED: {
            label: t('home.promo-status-approved', 'Approved'),
            color: '#22C55E',
          },
          REJECTED: {
            label: t('home.promo-status-rejected', 'Rejected'),
            color: '#EF4444',
          },
          PENDING: {
            label: t('home.promo-status-pending', 'Pending'),
            color: '#F59E0B',
          },
          PENDING_REVIEW: {
            label: t('home.promo-status-pending', 'Pending Review'),
            color: '#F59E0B',
          },
          WAITING_ACCEPTANCE: {
            label: t('home.promo-status-pending', 'Pending'),
            color: '#F59E0B',
          },
          ONGOING: {
            label: t('home.promo-status-ongoing', 'Ongoing'),
            color: '#06B6D4',
          },
          RETURNED: {
            label: t('home.promo-action-return', 'Returned'),
            color: '#F59E0B',
          },
          CANCELED: {
            label: record.statusName || 'Canceled',
            color: '#EF4444',
          },
        };
        const status = statusMap[rawStatus] || {
          label: record.statusName || record.status || '-',
          color: '#06B6D4',
        };
        return (
          <span style={{ color: status.color, fontWeight: 600 }}>
            {status.label}
          </span>
        );
      },
    },
  ];

  return (
    <div className="bg-white rounded-xl p-6 flex flex-col gap-6 shadow-sm border border-gray-100">
      <div className="flex flex-col gap-1">
        <h3 className="text-[#111928] text-2xl font-bold leading-none">
          {t('home.promo-requests-table-title')}
        </h3>
        <SectionDesc text={t('home.promo-requests-table-desc')} />
      </div>
      <CustomTable
        loading={isLoading}
        columns={columns}
        dataSource={data}
        rowKey={(record) => record.id || `${record.seller}-${record.product}`}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={totalElements}
        onPageChange={onPageChange}
        onRowClick={(_e, record) => onRowClick(record)}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
}
